import { useEffect, useMemo, useRef } from "react"

import { useFileContentAsText } from "@/hooks/use-file-content"
import {
  clearHighlights,
  highlightTextInContainer,
  scrollCitationHighlightIntoView,
} from "@/utils/dom-highlight"

import { FallbackViewer } from "./fallback-viewer"
import styles from "../styles/file-viewer.module.css"

type TextViewerProps = {
  fileUrl: string
  highlightQuote?: string | null
  highlightChunk?: string | null
  highlightSnippet?: string | null
  highlightScrollContainer?: HTMLElement | null
}

/**
 * Renders plain txt files
 */
export function TextViewer({
  fileUrl,
  highlightQuote = null,
  highlightChunk = null,
  highlightSnippet = null,
  highlightScrollContainer = null,
}: TextViewerProps) {
  const { data: content, isError, isLoading } = useFileContentAsText(fileUrl)
  const contentRef = useRef<HTMLDivElement>(null)
  const highlightSnippets = useMemo(
    () =>
      [highlightQuote, highlightChunk, highlightSnippet].filter(
        (snippet): snippet is string => typeof snippet === "string" && snippet.trim().length > 0
      ),
    [highlightQuote, highlightChunk, highlightSnippet]
  )

  useEffect(() => {
    const container = contentRef.current
    if (!container) return
    let cancelScroll = () => {}
    clearHighlights(container)

    if (highlightSnippets.length === 0) return

    let highlighted = false
    for (const snippet of highlightSnippets) {
      const result = highlightTextInContainer(container, snippet)
      if (result.found) {
        highlighted = true
        break
      }
      clearHighlights(container)
    }

    if (highlighted) {
      const mark = container.querySelector("mark.citation-highlight")
      if (mark instanceof HTMLElement) {
        cancelScroll = scrollCitationHighlightIntoView(mark, {
          preferredContainer: highlightScrollContainer ?? container,
          maxPasses: 2,
        })
      }
    }

    return () => {
      cancelScroll()
      if (contentRef.current) clearHighlights(contentRef.current)
    }
  }, [content, highlightScrollContainer, highlightSnippets])

  if (isError) {
    return <FallbackViewer fileUrl={fileUrl} />
  }

  if (isLoading) {
    return (
      <div className={styles.infoContainer}>
        <div className={styles.infoInnerContainer}>
          <h2 style={{ margin: 0, padding: "0 0.5rem" }}>Loading...</h2>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.infoContainer}>
      <div ref={contentRef} className={styles.documentContent}>
        <pre>{content || ""}</pre>
      </div>
    </div>
  )
}
